import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const getMatterportEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const modelId = urlObj?.searchParams?.get('m') || urlObj?.pathname?.split('/')?.filter(Boolean)?.pop();
    if (modelId && modelId?.length > 3) {
      return `https://my.matterport.com/show/?m=${modelId}&play=1&hl=1`;
    }
    return url;
  } catch {
    return url;
  }
};

const CATEGORY_COLORS = {
  infrastructure: 'bg-blue-500',
  education: 'bg-purple-500',
  sante: 'bg-red-500',
  commerce: 'bg-amber-500',
  loisirs: 'bg-emerald-500',
  transport: 'bg-slate-500',
  residentiel: 'bg-orange-500',
  autre: 'bg-gray-500',
};

const CommuneTourBadge = ({ communeName, communeId }) => {
  const [tours, setTours] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTour, setPreviewTour] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const hoverTimeout = useRef(null);
  const leaveTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!communeId || loaded) return;
    const fetchTours = async () => {
      try {
        const { data, error } = await supabase
          ?.from('communes')
          ?.select('matterport_tours')
          ?.eq('id', communeId)
          ?.single();
        if (!error && data) {
          const raw = data?.matterport_tours;
          let parsed = [];
          if (Array.isArray(raw)) parsed = raw;
          else if (typeof raw === 'string') {
            try { parsed = JSON.parse(raw); } catch { parsed = []; }
          }
          setTours(parsed);
        }
      } catch {}
      setLoaded(true);
    };
    fetchTours();
  }, [communeId, loaded]);

  if (!tours?.length) return null;

  const firstTour = tours?.[0];
  const embedUrl = getMatterportEmbedUrl(firstTour?.url);
  const dotColor = CATEGORY_COLORS?.[firstTour?.category] || 'bg-brand-gold';

  const handleMouseEnter = () => {
    clearTimeout(leaveTimeout?.current);
    hoverTimeout.current = setTimeout(() => {
      setPreviewTour(firstTour);
      setIframeReady(false);
      setShowPreview(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout?.current);
    leaveTimeout.current = setTimeout(() => {
      setShowPreview(false);
      setPreviewTour(null);
    }, 200);
  };

  const handleGoToCommune = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    navigate(`/commune-explorer?commune=${communeId}`);
  };

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 3D Badge (Glassmorphic, no hard border) */}
      <button
        onClick={handleGoToCommune}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95 select-none bg-white/10 backdrop-blur-md border-none outline-none group"
        title={`Visites immersives — ${communeName}`}
      >
        {/* 3D cube icon via CSS (Golden) */}
        <span
          className="inline-flex items-center justify-center transition-transform group-hover:rotate-12"
          style={{ width: 14, height: 14 }}
        >
          <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14">
            <polygon points="7,1 13,4.5 13,9.5 7,13 1,9.5 1,4.5" fill="none" stroke="#C9A84C" strokeWidth="1.2"/>
            <polyline points="7,1 7,7 13,4.5" stroke="#C9A84C" strokeWidth="1" fill="none" opacity="0.8"/>
            <polyline points="7,7 1,4.5" stroke="#C9A84C" strokeWidth="1" fill="none" opacity="0.6"/>
            <polyline points="7,7 7,13" stroke="#C9A84C" strokeWidth="1" fill="none" opacity="0.7"/>
          </svg>
        </span>
        <span className="tracking-widest">3D</span>
        {tours?.length > 1 && (
          <span className="ml-0.5 bg-brand-gold text-black rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-black leading-none">
            {tours?.length}
          </span>
        )}
      </button>

      {/* Hover Preview Panel (Dark Premium) */}
      {showPreview && previewTour && (
        <div
          className="absolute z-50 bottom-full mb-3 left-1/2 -translate-x-1/2 w-64 rounded-2xl overflow-hidden shadow-2xl border-none outline-none"
          style={{ background: '#111111' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Preview header */}
          <div className="px-4 py-3 flex items-center justify-between bg-white/[0.02]">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 animate-pulse ${dotColor}`} />
              <span className="text-white text-xs font-bold truncate">
                {previewTour?.title || 'Visite immersive'}
              </span>
            </div>
            <span className="text-white/30 text-[10px] font-bold uppercase tracking-widest ml-3 flex-shrink-0">
              {tours?.length} visite{tours?.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Matterport mini-embed */}
          <div className="relative w-full" style={{ height: 160 }}>
            {!iframeReady && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#050505] gap-3">
                <div className="w-6 h-6 border-2 border-white/10 border-t-brand-gold rounded-full animate-spin" />
                <span className="text-white/30 text-[10px] font-bold uppercase tracking-widest">Chargement…</span>
              </div>
            )}
            {embedUrl && (
              <iframe
                src={embedUrl}
                className="w-full h-full border-none outline-none"
                allow="xr-spatial-tracking; fullscreen"
                title={previewTour?.title}
                onLoad={() => setIframeReady(true)}
                style={{ opacity: iframeReady ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}
              />
            )}
            {!embedUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#050505]">
                <span className="text-white/30 text-xs font-light">Aperçu non disponible</span>
              </div>
            )}
            {/* Overlay gradient at bottom for smooth transition */}
            <div className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none bg-gradient-to-t from-[#111111] to-transparent" />
          </div>

          {/* Footer CTA */}
          <div className="px-4 py-3 flex items-center justify-between bg-[#111111]">
            <span className="text-white/50 text-[10px] uppercase tracking-widest font-bold truncate pr-2">
              {communeName}
            </span>
            <button
              onClick={handleGoToCommune}
              className="flex items-center gap-1.5 text-brand-gold text-[11px] font-bold hover:text-yellow-300 transition-colors uppercase tracking-widest flex-shrink-0"
            >
              Explorer
              <svg viewBox="0 0 12 12" fill="none" width="10" height="10">
                <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommuneTourBadge;